import { DDSTooltip, DDSTypography } from 'den-design-system'
import React, { useEffect, useRef, useState } from 'react'
import { isEqual, isNull } from 'lodash'

import ConfirmationPopup from '../../../../components/common/Exceptions/ConfirmationPopup'
import { Link } from '@mui/material'
import NoFilterImage from '../../../../assets/images/folder-not-found 1.svg'
import NoRecord from '../../../../components/common/NoContent/NoRecord'
import NoRecordImage from '../../../../assets/images/setting.svg'
import { ProjectType } from '../../../../enum/ProjectType.enum'
import Search from '../../../../components/New/Search/Search'
import Spinner from '../../../../components/common/Spinner/Spinner'
import StringConstants from '../../../../constants/StringConstants'
import Table from '../../../../components/common/DDS/Table/Table'
import { generateRandomId } from '../../../../utils/CommonUtils'
import { getAllQueryParamsAsObjectFromUrl } from '../../../../utils/UrlUtils'
import { getOrgTimeRange } from '../../../../utils/DateTimeUtils'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../global/newPallete'
import { severityColorMap } from '../../../../constants/ExceptionsPageConstants'
import { useStoreContext } from '../../../../store/StoreContext'

type SortOrder = 'ASC' | 'DESC' | 'CLEAR'

interface ExceptionLogsProps {
  fetchAnExceptionDetails: () => void
  tableData: any
  loading: boolean
  selectedRows: any
  setLoading: (loading: boolean) => void
  currentPage: number
  setCurrentPage: (page: number) => void
  searchTerm: string
  isSearchTriggered: any
  handleSearchChange: (event: any) => void
  handleSearchEnter: (event: any) => void
  setShowDropdown: (show: boolean) => void
  showDropdown: boolean
  handleOptionSelect: (searchString: any, searchType: any) => void
  LogTableColumns: any[]
  totalCount: any
  setSearchParams: (params: any) => void
  setSelectedRows: (rows: any[]) => void
  searchRecordLength: number
  routeExceptionLog: any
  updateSeverity: (applyToDefaultGroupSeverity?: boolean) => void
  setModalOpen: any
  preference: any
  modalOpen: any
  setActiveSortOrder: (field: any) => void
  setLoadingDate: (loading: boolean) => void

  setSearchTerm: (search: any) => void
  searchParams: any
  fetchAllExceptionLogs: (queryParam: any) => void
  fetchExceptionFilters: () => void
  activeTabIndex: any
  selectedAll: boolean
  setSelectedAll: (selectedAll: boolean) => void
}

const GroupedExceptionsLogs: React.FC<ExceptionLogsProps> = ({
  fetchAnExceptionDetails,
  isSearchTriggered,
  tableData,
  loading,
  setLoading,
  currentPage,
  setCurrentPage,
  searchTerm,
  handleSearchChange,
  handleSearchEnter,
  setShowDropdown,
  selectedRows,
  showDropdown,
  handleOptionSelect,
  LogTableColumns,
  totalCount,
  setSearchParams,
  setSelectedRows,
  searchRecordLength,
  routeExceptionLog,
  updateSeverity,
  setModalOpen,
  preference,
  modalOpen,
  setActiveSortOrder,
  setSearchTerm,
  searchParams,
  fetchAllExceptionLogs,
  fetchExceptionFilters,
  activeTabIndex,
  selectedAll,
  setSelectedAll,
  setLoadingDate
}) => {
  const store = useStoreContext()
  const tableRef = useRef<HTMLDivElement>(null)
  const [tableHeight, setTableHeight] = useState<number | null>(null)
  const projectType = store.scopeStore.getSelectedProject().type
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY
  const isSalesforceProject = projectType === ProjectType.SALESFORCE_ONLY

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlSortField = searchParams.get('sortField')
    const urlSortOrder = searchParams.get('sortOrder')

    if (urlSortField && urlSortOrder) {
      setActiveSortOrder({
        field: urlSortField,
        order: urlSortOrder as SortOrder
      })
    }
  }, [location.search])

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.clientHeight)
    }
  }, [tableData, loading])

  useEffect(() => {
    if (isSearchTriggered.current) {
      isSearchTriggered.current = false
      return
    }
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const orgTimeZone = store.userStore.getOrganizationPreference()?.timeZone

    const groupTab = searchParams.get('groupTab')

    const { startOfMonth, endOfCurrentDayTimestamp } =
      getOrgTimeRange(orgTimeZone)

    const searchString = allQueryParams['search-string'] || ''
    if (searchString) {
      setSearchTerm(searchString)
    }
    if (!allQueryParams['search-string']) {
      setSearchTerm('')
    }

    // Handle exception group tab parameters
    const exceptionGroupTabParam = sessionStorage.getItem('exceptionGroupTab')
    const exceptionGroupTab: Record<string, string> =
      exceptionGroupTabParam === '2' && groupTab === '2'
        ? {
            'is-bookmarked': sessionStorage.getItem('is-bookmarked') || '',
            severity: sessionStorage.getItem('severity') || '',
            'exception-status':
              sessionStorage.getItem('exception-status') || '',
            'assigned-to': sessionStorage.getItem('assigned-to') || ''
          }
        : {}

    const filteredExceptionGroupTab = Object.fromEntries(
      Object.entries(exceptionGroupTab).filter(([_, value]) => value !== '')
    )

    // Combine all parameters
    const params = {
      page: currentPage.toString(),
      ...allQueryParams,
      'created-from': allQueryParams['created-from'] || String(startOfMonth),
      'created-to':
        allQueryParams['created-to'] || String(endOfCurrentDayTimestamp),
      'time-zone': orgTimeZone,
      size: StringConstants.DEFAULT_TABLE_SIZE.toString(),
      ...filteredExceptionGroupTab
    }

    const pageFromQueryParams = searchParams.get('page')

    if (!isEqual(params, allQueryParams)) {
      setSearchParams(params)
      setCurrentPage(1)
    } else if (!isNull(pageFromQueryParams)) {
      fetchAllExceptionLogs({
        ...params,
        size: StringConstants.DEFAULT_TABLE_SIZE
      })
      setCurrentPage(Number(pageFromQueryParams))

      fetchExceptionFilters()
      fetchAnExceptionDetails()
    }
  }, [
    store.exceptionStore.getIsRefreshed(),
    currentPage,
    location.search,
    activeTabIndex
  ])

  useEffect(() => {
    if (store.filterStore.getFilterDisabled()) {
      store.filterStore.setFilterDisabled(false)
    }
  }, [store.filterStore.getFilterDisabled()])

  if (loading) return <Spinner />
  return (
    <div className='my-[24px]' ref={tableRef}>
      <div className='relative w-full pb-[24px]'>
        <Search
          id='search'
          name='Search'
          placeholder='Search'
          value={searchTerm}
          onChange={handleSearchChange}
          autoComplete='off'
          width='32vw'
          disabled={loading}
          onPressEnter={() => handleSearchEnter(searchTerm)}
          onBlur={() => {
            setTimeout(() => setShowDropdown(false), 200)
          }}
        />
        {showDropdown && (
          <div
            className='absolute z-[99999] mt-[12px] flex flex-col bg-white border items-start p-[12px] rounded-md w-[32vw] shadow-lg'
            style={{ borderColor: pallete.colors.stroke2 }}
          >
            <div
              className='flex p-[8px] gap-[8px] items-center cursor-pointer'
              onClick={() => handleOptionSelect(searchTerm, 'message')}
            >
              <DDSTooltip
                id={generateRandomId()}
                label={searchTerm}
                className={`border override-tooltip-arrow max-w-[90%] break-all ${
                  typeof searchTerm === 'string' && searchTerm.length > 38
                    ? searchTerm
                    : '!hidden'
                }`}
                theme='light'
                style={{
                  borderColor: pallete.colors.stroke2,
                  zIndex: 99999
                }}
              >
                <div className='flex flex-row  gap-[8px]'>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark4}
                    className='flex-start truncate  w-[250px] cursor-default'
                  >
                    {searchTerm}
                  </DDSTypography.Paragraph>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark6}
                  >
                    in message column
                  </DDSTypography.Paragraph>
                </div>
              </DDSTooltip>
            </div>
            <div
              className='flex p-[8px] gap-[8px] items-center cursor-pointer'
              onClick={() => handleOptionSelect(searchTerm, 'any')}
            >
              <DDSTooltip
                id={generateRandomId()}
                label={searchTerm}
                className={`border override-tooltip-arrow max-w-[90%] break-all ${
                  typeof searchTerm === 'string' && searchTerm.length > 38
                    ? searchTerm
                    : '!hidden'
                }`}
                theme='light'
                style={{
                  borderColor: pallete.colors.stroke2,
                  zIndex: 99999
                }}
              >
                <div className='flex flex-row  gap-[8px]'>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark4}
                    className='flex-start truncate  w-[250px] cursor-default'
                  >
                    {searchTerm}
                  </DDSTypography.Paragraph>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark6}
                  >
                    in all column
                  </DDSTypography.Paragraph>
                </div>
              </DDSTooltip>
            </div>
          </div>
        )}
      </div>
      {selectedRows.length === tableData.length &&
        selectedAll === false &&
        totalCount > 10 && (
          <div
            style={{ backgroundColor: pallete.colors.surface4 }}
            className='border-t border-l border-r rounded-t-lg py-[16px] px-[8px] justify-center flex items-center w-full text-center'
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: pallete.colors.textDark4
              }}
            >
              {selectedRows.length}{' '}
              {isFullStackProject ? 'exceptions' : 'errors'} on this page
              selected.&nbsp;
            </span>
            <span
              className='underline cursor-pointer'
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: pallete.colors.primary2
              }}
              onClick={() => {
                setSelectedAll(true)
                setSelectedRows([])
              }}
            >
              Select all {totalCount}{' '}
              {isFullStackProject ? 'exceptions' : 'errors'} that match this
              search query
            </span>
          </div>
        )}

      {selectedAll && (
        <div
          style={{ backgroundColor: pallete.colors.surface4 }}
          className='border-t border-l border-r rounded-t-lg py-[16px] px-[8px] justify-center flex items-center w-full text-center'
        >
          <span
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: pallete.colors.textDark4
            }}
          >
            Selected all {totalCount} issues that match this search query.&nbsp;
          </span>
          <span
            className='underline cursor-pointer'
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: pallete.colors.primary2
            }}
            onClick={() => {
              setSelectedAll(false)
              setSelectedRows([])
              fetchAllExceptionLogs({
                size: StringConstants.DEFAULT_TABLE_SIZE
              })
            }}
          >
            Clear selection
          </span>
        </div>
      )}

      {tableData.length > 0 ? (
        <Table
          data={tableData}
          padding={!selectedAll && selectedRows.length !== tableData.length}
          columns={LogTableColumns}
          totalCount={totalCount}
          rowsPerPage={10}
          currentPage={currentPage}
          onPageChange={(page) => {
            const allQueryParams = getAllQueryParamsAsObjectFromUrl(
              location.search
            )

            setSearchParams({ ...allQueryParams, page: page.toString() })
            setCurrentPage(page)
            setLoading(true)
          }}
          selectable
          tableStyle={{ overflowX: 'scroll' }}
          loading={loading}
          selectionType='checkbox'
          onSelect={(row: any) => {
            setSelectedRows(row)
            setSelectedAll(false)
          }}
          clickType='GroupExceptionLog'
          clickable={routeExceptionLog}
        />
      ) : searchRecordLength === 0 && searchTerm != '' ? (
        <NoRecord
          style={{ height: '70vh' }}
          imageSrc={NoFilterImage}
          text='No results found'
        />
      ) : (
        <NoRecord
          imageSrc={
            store.filterStore.getFiltersApplied()
              ? NoFilterImage
              : NoRecordImage
          }
          style={{ height: '50vh' }}
          text={
            store.filterStore.getFiltersApplied() ? (
              StringConstants.NO_RECORDS_FILTER
            ) : (
              <>
                Configure {isFullStackProject ? 'Exception' : 'Error'}
                {''} Monitoring.
                <Link
                  href={
                    isFullStackProject
                      ? StringConstants.FULLSTACK_URL
                      : isSalesforceProject
                        ? StringConstants.SALESFORCE_URL
                        : StringConstants.MULESOFT
                  }
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ marginLeft: '4px' }}
                >
                  Learn more
                </Link>
              </>
            )
          }
        />
      )}
      {!selectedAll ? (
        <ConfirmationPopup
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          title='Severity Type'
          loading={loading}
          primaryButtonDisabled={loading}
          secondaryButtonDisabled={loading}
          primaryButtonClick={() => updateSeverity()}
          secondaryButtonClick={() => {
            setModalOpen(false)
          }}
          primaryButtonText='Yes'
          secondaryButtonText='No'
          content={
            <div className='flex flex-col w-full gap-y-[24px] items-center justify-center '>
              <div>
                <DDSTypography.Title
                  variant='semiBold'
                  type='h4'
                  className='text-center flex flex-row whitespace-nowrap gap-x-[4px]'
                >
                  {isFullStackProject
                    ? 'This will change severity type for selected exceptions to'
                    : 'This will change severity type for selected errors to'}
                  <DDSTypography.Title
                    type='h4'
                    variant='semiBold'
                    color={severityColorMap[preference]}
                  >
                    {preference}
                  </DDSTypography.Title>
                </DDSTypography.Title>
              </div>
            </div>
          }
        />
      ) : (
        <ConfirmationPopup
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          title='Severity Type'
          loading={loading}
          primaryButtonDisabled={loading}
          secondaryButtonDisabled={loading}
          primaryButtonClick={() => {
            updateSeverity(true)
          }}
          secondaryButtonClick={() => {
            updateSeverity(false)
          }}
          primaryButtonText='Yes'
          secondaryButtonText='No'
          content={
            <div className='flex flex-col w-full gap-y-[24px] items-center justify-center '>
              <div>
                <DDSTypography.Title
                  variant='semiBold'
                  type='h4'
                  className='text-center flex flex-row whitespace-nowrap gap-x-[4px]'
                >
                  Do you also want to change the default group severity?
                </DDSTypography.Title>
              </div>
            </div>
          }
        />
      )}
    </div>
  )
}
export default observer(GroupedExceptionsLogs)
